import React from 'react';
import { Document, Page } from 'react-pdf';

import {
  Box,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  HStack,
  NewButton,
  Skeleton,
  Text,
  VStack,
} from '@eluve/components';

import { usePdfContext } from './PdfContext';

const LoadingSkeleton = () => (
  <VStack align="center" justify="center" gap={4} className="p-8">
    <Skeleton className="h-[600px] w-[450px]" />
    <Skeleton className="h-4 w-32" />
  </VStack>
);

const ErrorMessage: React.FC<{ message: string }> = ({ message }) => (
  <HStack align="center" justify="center" className="p-8">
    <Text size="md" color="primary">
      {message}
    </Text>
  </HStack>
);

export const PdfPreviewDialog: React.FC = () => {
  const {
    showPreview,
    setShowPreview,
    currentPreviewFile,
    currentPage,
    totalPages,
    loading,
    error,
    onDocumentLoadSuccess,
    textRenderer,
    handlePrevPage,
    handleNextPage,
  } = usePdfContext();
  return (
    <Dialog open={showPreview} onOpenChange={setShowPreview}>
      <DialogContent className="h-[80vh] max-w-4xl">
        <DialogHeader>
          <DialogTitle>{currentPreviewFile?.fileName}</DialogTitle>
        </DialogHeader>

        {/*  Navigation */}
        {totalPages > 1 && (
          <HStack gap={3} justify="center" className="border-b pb-4">
            <NewButton
              text="← Prev"
              type="outline"
              size="s"
              onClick={handlePrevPage}
              disabled={currentPage <= 1}
            />
            <Text size="sm" color="secondary">
              Page {currentPage} of {totalPages}
            </Text>
            <NewButton
              text="Next →"
              type="outline"
              size="s"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages}
            />
          </HStack>
        )}

        <Box className="flex-1 overflow-auto">
          <div className="flex justify-center">
            {loading && <LoadingSkeleton />}

            {error && <ErrorMessage message={error} />}

            {!loading && !error && currentPreviewFile?.fileUrl && (
              <Document
                file={currentPreviewFile.fileUrl}
                onLoadSuccess={onDocumentLoadSuccess}
              >
                <Page
                  pageNumber={currentPage}
                  scale={1.2}
                  customTextRenderer={textRenderer}
                  loading={<LoadingSkeleton />}
                  error={
                    <ErrorMessage
                      message={`Failed to load page ${currentPage}`}
                    />
                  }
                />
              </Document>
            )}
          </div>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
